import { ActionTree, ActionContext } from 'vuex';
import { RootState } from '@/store';
import { State, state } from './state';
import { Mutations } from './mutations';
import { MenuActionTypes } from './action-types';
import { MenuMutationTypes } from './mutation-types';
import { LoaderActionTypes } from '../loader/action-types';
import { AuthActionTypes } from '../auth/action-types';
import usuarioService from '@/services/seguranca/seg-usuario.service';
import router from '@/routes';
import type { RouteParamsRawGeneric } from 'vue-router';

type AugmentedActionContext = {
  commit<K extends keyof Mutations>(key: K, payload: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

type SetObraPayload = {
  obra: object;
  redirectToParent?: boolean;
};

type SetContratoPayload = {
  contrato: number;
  redirectToParent?: boolean;
};

const getParentRouteParams = (parentPath: string, currentParams: RouteParamsRawGeneric): RouteParamsRawGeneric => {
  const params: RouteParamsRawGeneric = {};

  for (const match of parentPath.matchAll(/:([A-Za-z0-9_]+)/g)) {
    const paramName = match[1];
    const value = currentParams[paramName];
    if (value != null) params[paramName] = value;
  }

  return params;
};

const redirectToParentRoute = async (): Promise<boolean> => {
  const currentRoute = router.currentRoute.value;
  const parentRouteName = currentRoute.meta?.parentRouteName;

  if (typeof parentRouteName !== 'string' || !parentRouteName) return false;

  const parentRecord = router.getRoutes().find((route) => route.name === parentRouteName);
  if (!parentRecord) throw new Error(`Rota pai '${parentRouteName}' nao encontrada.`);

  await router.replace({
    name: parentRouteName,
    params: getParentRouteParams(parentRecord.path, currentRoute.params),
    query: currentRoute.query
  });

  return true;
};

export interface Actions {
  [MenuActionTypes.ACTION_SET_OBRA]({ commit }: AugmentedActionContext, payload: SetObraPayload): Promise<boolean>;

  [MenuActionTypes.ACTION_SET_CONTRATO]({ commit, dispatch }: AugmentedActionContext, payload: SetContratoPayload): Promise<boolean>;
  [MenuActionTypes.ACTION_SET_CONTRATOS]({ commit }: AugmentedActionContext, contratos: any[]): Promise<boolean>;
  [MenuActionTypes.ACTION_SET_OBRAS]({ commit }: AugmentedActionContext, contratos: any[]): Promise<boolean>;
}

export const actions: ActionTree<State, RootState> & Actions = {
  async [MenuActionTypes.ACTION_SET_OBRA]({ commit }, { obra, redirectToParent = false }: SetObraPayload): Promise<boolean> {
    if (redirectToParent) {
      if ((obra as any)?.gesObraId == (state.obraSelecionada as any)?.gesObraId) return true;
      await redirectToParentRoute();
    }

    commit(MenuMutationTypes.SET_OBRA, obra);
    return true;
  },
  async [MenuActionTypes.ACTION_SET_CONTRATOS]({ commit, dispatch }, contratos: any[]): Promise<boolean> {
    try {
      if ((!state.codigoContrato && contratos.length > 0) || (contratos.length > 0 && !contratos.find((x) => x.valor == state.codigoContrato))) {
        commit(MenuMutationTypes.SET_CONTRATO, contratos[0].valor);
      }

      commit(MenuMutationTypes.SET_CONTRATOS_USUARIO, contratos);
      const result = await usuarioService.getObrasAcessoUsuario();
      dispatch(MenuActionTypes.ACTION_SET_OBRAS, result.data);
      return Promise.resolve(true);
    } catch {
      return Promise.resolve(false);
    }
  },
  async [MenuActionTypes.ACTION_SET_OBRAS]({ commit }, obras: any[]): Promise<boolean> {
    const obraAtualId = (state.obraSelecionada as any)?.gesObraId;

    if (obras.length > 0) {
      const obraAtualAindaExiste = obras.some((x) => x.gesObraId == obraAtualId);
      if (!obraAtualId || !obraAtualAindaExiste) {
        commit(MenuMutationTypes.SET_OBRA, obras[0]);
      }
    } else {
      const obraAtualTemValor = !!state.obraSelecionada && Object.keys(state.obraSelecionada as any).length > 0;
      if (obraAtualTemValor) {
        commit(MenuMutationTypes.SET_OBRA, {});
      }
    }

    commit(MenuMutationTypes.SET_OBRAS_USUARIO, obras);
    return Promise.resolve(true);
  },
  async [MenuActionTypes.ACTION_SET_CONTRATO]({ commit, dispatch }, { contrato, redirectToParent = false }: SetContratoPayload): Promise<boolean> {
    if (redirectToParent) {
      if (contrato == state.codigoContrato) return true;
      await redirectToParentRoute();
    }

    commit(MenuMutationTypes.SET_CONTRATO, contrato);
    if (router.currentRoute.value.name != 'proxyLegado') {
      dispatch(LoaderActionTypes.ACTION_SHOW);

      await dispatch(AuthActionTypes.ACTION_UPDATE_USUARIO_ID);

      const result = await usuarioService.getObrasAcessoUsuario();
      commit(MenuMutationTypes.SET_OBRAS_USUARIO, result.data ?? []);
      commit(MenuMutationTypes.SET_OBRA, result.data?.length > 0 ? result.data[0] : {});
      dispatch(LoaderActionTypes.ACTION_HIDE);
    }

    return true;
  }
};
